import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Icon from './Icon';
import { readSaved, save } from '../gameDeck';

// Fullscreen is optional. The controls must fit even when iPhone keeps its bars.
export function useGameDisplay(active, cardKey) {
  const [expanded, setExpanded] = useState(() => readSaved('diyt-full-screen-v1', false) === true);
  const contentRef = useRef(null);
  const ownsFullscreen = useRef(false);
  const activeRef = useRef(active);
  const mountedRef = useRef(false);
  const generationRef = useRef(0);
  const intentRef = useRef(expanded);
  const [pending, setPending] = useState(false);

  useLayoutEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  async function exitNative() {
    try { if (document.fullscreenElement === document.documentElement) await document.exitFullscreen?.(); } catch {}
  }

  useLayoutEffect(() => {
    activeRef.current = active;
    if (!active) return;
    const root = document.documentElement;
    const viewport = window.visualViewport;
    const resize = () => {
      // Let users pinch and pan normally instead of reflowing under their fingers.
      if (viewport && Math.abs(viewport.scale - 1) > 0.01) return;
      root.style.setProperty('--game-viewport-height', `${viewport?.height || window.innerHeight}px`);
    };
    root.classList.add('game-display-active');
    resize();
    window.addEventListener('resize', resize);
    viewport?.addEventListener('resize', resize);
    return () => {
      activeRef.current = false;
      generationRef.current += 1;
      root.classList.remove('game-display-active', 'game-display-expanded');
      root.style.removeProperty('--game-viewport-height');
      window.removeEventListener('resize', resize);
      viewport?.removeEventListener('resize', resize);
      if (ownsFullscreen.current && document.fullscreenElement && document.exitFullscreen) {
        exitNative();
      }
      ownsFullscreen.current = false;
    };
  }, [active]);

  useLayoutEffect(() => {
    intentRef.current = active && expanded;
    document.documentElement.classList.toggle('game-display-expanded', active && expanded);
    return () => document.documentElement.classList.remove('game-display-expanded');
  }, [active, expanded]);

  useLayoutEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [cardKey]);

  useEffect(() => {
    if (!active) return;
    const sharedDisplay = () => { if (window.RecessDisplay) {setExpanded(window.RecessDisplay.get().expanded);setPending(window.RecessDisplay.get().pending);} };
    document.addEventListener('recess-display-change', sharedDisplay);
    document.addEventListener('recess-ready', sharedDisplay);
    sharedDisplay();
    const fullscreenChanged = () => {
      if (ownsFullscreen.current && !document.fullscreenElement) {
        ownsFullscreen.current = false;
        intentRef.current = false;
        setExpanded(false);
        save('diyt-full-screen-v1', false);
      }
    };
    const escape = event => {
      if (event.key === 'Escape' && !event.defaultPrevented && !document.fullscreenElement) {
        intentRef.current = false;
        setExpanded(false);
        save('diyt-full-screen-v1', false);
      }
    };
    document.addEventListener('fullscreenchange', fullscreenChanged);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('recess-display-change', sharedDisplay);
      document.removeEventListener('recess-ready', sharedDisplay);
      document.removeEventListener('fullscreenchange', fullscreenChanged);
      document.removeEventListener('keydown', escape);
    };
  }, [active]);

  async function toggle() {
    if (window.RecessDisplay) { await window.RecessDisplay.toggleFull(); return; }
    if (pending) return;
    const generation = generationRef.current;
    const next = !expanded;
    intentRef.current = next;
    setExpanded(next);
    save('diyt-full-screen-v1', next);
    try {
      if (next && document.fullscreenEnabled && document.documentElement.requestFullscreen) {
        setPending(true);
        ownsFullscreen.current = true;
        await document.documentElement.requestFullscreen();
        // A navigation or Escape can happen before the browser resolves this promise.
        if (!activeRef.current || !intentRef.current || generation !== generationRef.current) {
          await exitNative();
          ownsFullscreen.current = false;
        }
      } else if (!next && ownsFullscreen.current && document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch {
      // Unsupported or declined native fullscreen still leaves a usable focus view.
      ownsFullscreen.current = false;
    } finally {
      if (mountedRef.current) setPending(false);
    }
  }
  return { expanded, toggle, contentRef, pending };
}

export function FullScreenButton({ display }) {
  return <button className="back-link display-toggle" onClick={display.toggle} disabled={display.pending} aria-busy={display.pending} aria-pressed={display.expanded} aria-label={display.expanded ? 'Exit full screen' : 'Full screen'} title="Full screen. On phones, browser controls may remain visible.">
    <Icon name={display.expanded ? 'collapse' : 'expand'} />
    <span>{display.expanded ? 'Exit full screen' : 'Full screen'}</span>
  </button>;
}
